/**
 * Discounturi acordate → fila „Campanii": costul si efectul campaniilor B2B.
 *
 * Campania (tip + perioada) se citeste din observatia facturii de reducere; ce nu se poate citi
 * sta la „De incadrat", unde adminul alege tipul si perioada. Efectul = vanzarile clientilor in
 * perioada campaniei fata de perioada egala de dinainte.
 *
 * Design: docs/superpowers/specs/2026-11-02-discounturi-campanii-design.md
 */
import { Fragment, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'

type TipCampanie =
  | 'bonus_volum'
  | 'promo_pacient'
  | 'incentivare'
  | 'promotionala'
  | 'co_branding'
  | 'diferenta_pret'
  | 'nespecificat'

const TIPURI: [TipCampanie, string][] = [
  ['bonus_volum', 'Bonus de volum'],
  ['promo_pacient', 'Promo pacient'],
  ['incentivare', 'Incentivare'],
  ['promotionala', 'Campanie promoțională'],
  ['co_branding', 'Co-branding'],
  ['diferenta_pret', 'Diferență de preț'],
  ['nespecificat', 'Nespecificat'],
]

interface Factura {
  docNo: string
  data: string
  partener: string
  cost: number
  nota: string | null
  sursa?: 'manual' | 'nota' | 'storno' | null
  motiv?: string | null
  esteStorno: boolean
}

interface ClientCampanie {
  client: string
  partener: string
  cost: number
  vanzariCampanie: number
  vanzariInainte: number | null
  diferenta: number | null
  referintaCuCampanie: boolean
}

interface Campanie {
  cheie: string
  eticheta: string
  tipCampanie: TipCampanie
  de: string
  pana: string
  referinta: { de: string; pana: string } | null
  inCurs: boolean
  cost: number
  clienti: ClientCampanie[]
  vanzariCampanie: number
  vanzariInainte: number | null
  diferenta: number | null
  leiPeLeu: number | null
  procentDinVanzari: number | null
  referintaCuCampanie: boolean
  areManuale: boolean
  facturataDe: string
  facturataPana: string
  facturi: Factura[]
}

interface Rezumat {
  tipCampanie: TipCampanie
  eticheta: string
  cost: number
  campanii: number
  clienti: number
  medianaLeiPeLeu: number | null
}

interface Raport {
  rezumat: Rezumat[]
  campanii: Campanie[]
  deIncadrat: { facturi: Factura[]; cost: number }
}

const MOTIV: Record<string, string> = {
  fara_rand_spv: 'factura nu e în SPV-ul din CRM',
  xml_lipsa: 'XML-ul facturii lipsește din CRM',
  fara_observatii: 'factura n-are observații',
  nota_necitita: 'observația nu spune o perioadă',
}

const lei = (n: number | null) =>
  n == null ? '—' : n.toLocaleString('ro-RO', { maximumFractionDigits: 0 })
const zi = (s: string) => `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}`
const pct = (n: number | null) =>
  n == null ? '—' : `${n.toLocaleString('ro-RO', { maximumFractionDigits: 1 })}%`
const leuPeLeu = (n: number | null) =>
  n == null
    ? '—'
    : n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

function Semn({ text, title }: { text: string; title: string }) {
  return (
    <span
      title={title}
      className="ml-1 whitespace-nowrap rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600"
    >
      {text}
    </span>
  )
}

export function FilaCampaniiDiscount({
  params,
  de,
  pana,
  poateScrie,
}: {
  params: string
  de: string
  pana: string
  poateScrie: boolean
}) {
  const [deschis, setDeschis] = useState<string | null>(null)
  const [ordine, setOrdine] = useState<'cost' | 'leiPeLeu'>('cost')
  const [incadrare, setIncadrare] = useState(false)

  const q = useQuery<Raport>({
    queryKey: ['discounturi', 'campanii', de, pana],
    queryFn: async () =>
      (await api.get(`/discounturi/campanii?${params}`, { timeout: 120_000 })).data,
  })

  const lista = useMemo(() => {
    const l = [...(q.data?.campanii ?? [])]
    if (ordine === 'leiPeLeu')
      l.sort((a, b) => (b.leiPeLeu ?? -Infinity) - (a.leiPeLeu ?? -Infinity))
    return l
  }, [q.data, ordine])

  if (q.isLoading) return <p className="py-6 text-center text-sm text-slate-400">Se calculează…</p>
  if (q.isError || !q.data)
    return (
      <p className="py-6 text-center text-sm text-red-600">Nu s-au putut încărca campaniile.</p>
    )
  const { rezumat, deIncadrat } = q.data

  return (
    <div className="space-y-4">
      {deIncadrat.facturi.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900">
          <span>
            <b>{deIncadrat.facturi.length}</b> facturi de reducere,{' '}
            <b>{lei(deIncadrat.cost)} lei</b>, n-au campanie (după data facturii, în perioada
            aleasă).
          </span>
          <button
            className="rounded border border-amber-400 bg-white px-3 py-1 font-medium hover:bg-amber-100"
            onClick={() => setIncadrare((v) => !v)}
          >
            {incadrare ? 'Ascunde' : poateScrie ? 'Încadrează' : 'Vezi lista'}
          </button>
        </div>
      )}
      {incadrare && <DeIncadrat facturi={deIncadrat.facturi} poateScrie={poateScrie} />}

      {rezumat.length === 0 ? (
        <Card className="p-6 text-center text-sm text-slate-500">
          Nicio campanie în perioada aleasă.
        </Card>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {rezumat.map((r) => (
              <Card key={r.tipCampanie} className="p-4">
                <div className="text-sm font-medium text-slate-700">{r.eticheta}</div>
                <div className="mt-1 text-xl font-semibold">{lei(r.cost)} lei</div>
                <div className="text-xs text-slate-500">
                  {r.campanii} campanii · {r.clienti} clienți
                </div>
                <div
                  className={`mt-1 text-xs ${r.tipCampanie === 'bonus_volum' ? 'text-slate-400' : 'text-slate-700'}`}
                  title={
                    r.tipCampanie === 'bonus_volum'
                      ? 'La bonusul de volum vânzările mari sunt cauza reducerii, nu efectul ei.'
                      : 'Mediana pe campaniile tipului: cât s-a vândut în plus pentru fiecare leu de reducere.'
                  }
                >
                  lei vânduți în plus / leu: <b>{leuPeLeu(r.medianaLeiPeLeu)}</b> (mediană)
                </div>
              </Card>
            ))}
          </div>
          <p className="text-xs text-slate-500">
            Vânzările în plus nu se adună pe tip: un client poate fi în două campanii suprapuse, iar
            aceleași vânzări s-ar număra de două ori. Vânzările sunt sell-in (ce cumpără clientul de
            la noi), fără TVA; „înainte” = perioada de aceeași lungime de dinaintea campaniei.
          </p>

          <Card className="overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="border-b bg-slate-50 text-left text-slate-500">
                <tr>
                  <th className="px-3 py-2">Campanie</th>
                  <th className="px-3 py-2 text-right">Clienți</th>
                  <th
                    className="cursor-pointer px-3 py-2 text-right hover:text-slate-900"
                    onClick={() => setOrdine('cost')}
                  >
                    Cost {ordine === 'cost' && '▾'}
                  </th>
                  <th className="px-3 py-2 text-right">Vânzări în campanie</th>
                  <th className="px-3 py-2 text-right">Vânzări înainte</th>
                  <th className="px-3 py-2 text-right">Diferență</th>
                  <th
                    className="cursor-pointer px-3 py-2 text-right hover:text-slate-900"
                    onClick={() => setOrdine('leiPeLeu')}
                    title="Cât s-a vândut în plus pentru fiecare leu de reducere (nu e profit)"
                  >
                    Lei / leu {ordine === 'leiPeLeu' && '▾'}
                  </th>
                  <th className="px-3 py-2">Facturată</th>
                </tr>
              </thead>
              <tbody>
                {lista.map((c) => {
                  const eDeschis = deschis === c.cheie
                  const bonus = c.tipCampanie === 'bonus_volum'
                  return (
                    <Fragment key={c.cheie}>
                      <tr
                        className="cursor-pointer border-b hover:bg-slate-50"
                        onClick={() => setDeschis(eDeschis ? null : c.cheie)}
                      >
                        <td className="px-3 py-2 font-medium">
                          {eDeschis ? '▾' : '▸'} {c.eticheta}
                          {c.inCurs && (
                            <Semn
                              text="în curs"
                              title="Perioada nu s-a încheiat: cifrele sunt de până azi."
                            />
                          )}
                          {!c.referinta && (
                            <Semn
                              text="fără referință"
                              title="Perioada de dinainte e înainte de mai 2025, fără date B2B în CRM."
                            />
                          )}
                          {c.referintaCuCampanie && (
                            <Semn
                              text="referința are campanie"
                              title="Cel puțin un client avea altă campanie în perioada de dinainte: comparația e orientativă."
                            />
                          )}
                          {c.areManuale && (
                            <Semn
                              text="încadrat manual"
                              title="Cel puțin o factură a fost pusă de mână pe campanie."
                            />
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">{c.clienti.length}</td>
                        <td className="px-3 py-2 text-right font-semibold">{lei(c.cost)}</td>
                        <td className="px-3 py-2 text-right">{lei(c.vanzariCampanie)}</td>
                        <td className="px-3 py-2 text-right">{lei(c.vanzariInainte)}</td>
                        <td
                          className={`px-3 py-2 text-right ${c.diferenta != null && c.diferenta < 0 ? 'text-red-600' : ''}`}
                        >
                          {c.diferenta == null
                            ? '—'
                            : `${c.diferenta > 0 ? '+' : ''}${lei(c.diferenta)}`}
                          {c.diferenta != null && c.vanzariInainte ? (
                            <span className="ml-1 text-xs text-slate-400">
                              {pct((c.diferenta / c.vanzariInainte) * 100)}
                            </span>
                          ) : null}
                        </td>
                        <td
                          className={`px-3 py-2 text-right ${bonus ? 'text-slate-400' : 'font-medium'}`}
                          title={
                            bonus
                              ? `Bonus de volum: reducerea e ${pct(c.procentDinVanzari)} din vânzări; vânzările sunt cauza ei, nu efectul.`
                              : undefined
                          }
                        >
                          {bonus ? pct(c.procentDinVanzari) : leuPeLeu(c.leiPeLeu)}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">
                          {c.facturataDe === c.facturataPana
                            ? zi(c.facturataDe)
                            : `${zi(c.facturataDe)} – ${zi(c.facturataPana)}`}
                        </td>
                      </tr>
                      {eDeschis && (
                        <tr className="border-b bg-slate-50/60">
                          <td colSpan={8} className="px-3 py-3">
                            <DetaliuCampanie c={c} poateScrie={poateScrie} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </Card>
          <p className="text-xs text-slate-500">
            La <b>bonusul de volum</b> coloana „Lei / leu” arată reducerea ca procent din vânzări:
            bonusul se dă pe cât a cumpărat clientul, deci vânzările nu sunt efectul lui.
          </p>
        </>
      )}
    </div>
  )
}

function DetaliuCampanie({ c, poateScrie }: { c: Campanie; poateScrie: boolean }) {
  const [editeaza, setEditeaza] = useState<string | null>(null)
  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500">
        Campania: {zi(c.de)} – {zi(c.pana)}
        {c.referinta && ` · comparată cu ${zi(c.referinta.de)} – ${zi(c.referinta.pana)}`}
      </div>
      <table className="w-full text-xs">
        <thead className="text-left text-slate-500">
          <tr>
            <th className="px-2 py-1">Client</th>
            <th className="px-2 py-1 text-right">Reducere</th>
            <th className="px-2 py-1 text-right">Vânzări în campanie</th>
            <th className="px-2 py-1 text-right">Vânzări înainte</th>
            <th className="px-2 py-1 text-right">Diferență</th>
          </tr>
        </thead>
        <tbody>
          {c.clienti.map((x) => (
            <tr key={x.client} className="border-t border-slate-200">
              <td className="px-2 py-1">
                {x.partener}
                {x.referintaCuCampanie && (
                  <Semn
                    text="referința are campanie"
                    title="Clientul avea altă campanie în perioada de dinainte."
                  />
                )}
              </td>
              <td className="px-2 py-1 text-right">{lei(x.cost)}</td>
              <td className="px-2 py-1 text-right">{lei(x.vanzariCampanie)}</td>
              <td className="px-2 py-1 text-right">{lei(x.vanzariInainte)}</td>
              <td
                className={`px-2 py-1 text-right ${x.diferenta != null && x.diferenta < 0 ? 'text-red-600' : ''}`}
              >
                {x.diferenta == null ? '—' : `${x.diferenta > 0 ? '+' : ''}${lei(x.diferenta)}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <table className="w-full text-xs">
        <thead className="text-left text-slate-500">
          <tr>
            <th className="px-2 py-1">Data</th>
            <th className="px-2 py-1">Factura</th>
            <th className="px-2 py-1">Client</th>
            <th className="px-2 py-1">Detaliu pe factură</th>
            <th className="px-2 py-1 text-right">Reducere</th>
            {poateScrie && <th className="px-2 py-1" />}
          </tr>
        </thead>
        <tbody>
          {c.facturi.map((f) => (
            <Fragment key={f.docNo}>
              <tr className="border-t border-slate-200">
                <td className="px-2 py-1">{zi(f.data)}</td>
                <td className="px-2 py-1">{f.docNo}</td>
                <td className="px-2 py-1">{f.partener}</td>
                <td className="px-2 py-1 text-slate-600">
                  {f.nota ?? '—'}
                  {f.sursa === 'manual' && <Semn text="manual" title="Încadrată de mână" />}
                  {f.sursa === 'storno' && (
                    <Semn text="storno" title="Ia campania facturii stornate" />
                  )}
                </td>
                <td className="px-2 py-1 text-right">{lei(f.cost)}</td>
                {poateScrie && (
                  <td className="px-2 py-1 text-right">
                    <button
                      className="text-b4l-blue hover:underline"
                      onClick={() => setEditeaza(editeaza === f.docNo ? null : f.docNo)}
                    >
                      Schimbă
                    </button>
                  </td>
                )}
              </tr>
              {editeaza === f.docNo && (
                <tr>
                  <td colSpan={6} className="px-2 py-2">
                    <FormIncadrare
                      docNo={f.docNo}
                      manual={f.sursa === 'manual'}
                      onGata={() => setEditeaza(null)}
                    />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function DeIncadrat({ facturi, poateScrie }: { facturi: Factura[]; poateScrie: boolean }) {
  const [editeaza, setEditeaza] = useState<string | null>(null)
  return (
    <Card className="overflow-x-auto p-0">
      <table className="w-full text-sm">
        <thead className="border-b bg-slate-50 text-left text-slate-500">
          <tr>
            <th className="px-3 py-2">Data</th>
            <th className="px-3 py-2">Factura</th>
            <th className="px-3 py-2">Client</th>
            <th className="px-3 py-2">Detaliu pe factură</th>
            <th className="px-3 py-2">De ce</th>
            <th className="px-3 py-2 text-right">Reducere</th>
            {poateScrie && <th className="px-3 py-2" />}
          </tr>
        </thead>
        <tbody>
          {facturi.map((f) => (
            <Fragment key={f.docNo}>
              <tr className="border-b">
                <td className="px-3 py-2">{zi(f.data)}</td>
                <td className="px-3 py-2">
                  {f.docNo}
                  {f.esteStorno && <Semn text="storno" title="Factură de storno" />}
                </td>
                <td className="px-3 py-2">{f.partener}</td>
                <td className="px-3 py-2 text-slate-600">{f.nota ?? '—'}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{MOTIV[f.motiv ?? ''] ?? '—'}</td>
                <td className="px-3 py-2 text-right">{lei(f.cost)}</td>
                {poateScrie && (
                  <td className="px-3 py-2 text-right">
                    <button
                      className="text-b4l-blue hover:underline"
                      onClick={() => setEditeaza(editeaza === f.docNo ? null : f.docNo)}
                    >
                      Încadrează
                    </button>
                  </td>
                )}
              </tr>
              {editeaza === f.docNo && (
                <tr className="border-b bg-slate-50/60">
                  <td colSpan={7} className="px-3 py-2">
                    <FormIncadrare
                      docNo={f.docNo}
                      manual={false}
                      onGata={() => setEditeaza(null)}
                    />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </Card>
  )
}

const doua = (n: number) => String(n).padStart(2, '0')
const ultimaZi = (an: number, luna: number) => new Date(Date.UTC(an, luna, 0)).getUTCDate()

/** Tipul si perioada: o luna, un trimestru sau un interval de zile. */
function FormIncadrare({
  docNo,
  manual,
  onGata,
}: {
  docNo: string
  manual: boolean
  onGata: () => void
}) {
  const qc = useQueryClient()
  const anAcum = new Date().getFullYear()
  const [tip, setTip] = useState<TipCampanie>('promo_pacient')
  const [fel, setFel] = useState<'luna' | 'trimestru' | 'interval'>('luna')
  const [luna, setLuna] = useState(`${anAcum}-${doua(new Date().getMonth() + 1)}`)
  const [an, setAn] = useState(anAcum)
  const [trim, setTrim] = useState(1)
  const [deZi, setDeZi] = useState('')
  const [panaZi, setPanaZi] = useState('')

  const perioada = (): { de: string; pana: string } | null => {
    if (fel === 'luna') {
      const [a, l] = luna.split('-').map(Number) as [number, number]
      if (!a || !l) return null
      return { de: `${a}-${doua(l)}-01`, pana: `${a}-${doua(l)}-${doua(ultimaZi(a, l))}` }
    }
    if (fel === 'trimestru') {
      const l1 = trim * 3 - 2
      return {
        de: `${an}-${doua(l1)}-01`,
        pana: `${an}-${doua(l1 + 2)}-${doua(ultimaZi(an, l1 + 2))}`,
      }
    }
    return deZi && panaZi && deZi <= panaZi ? { de: deZi, pana: panaZi } : null
  }

  const salveaza = useMutation({
    mutationFn: async () => {
      const p = perioada()
      if (!p) throw new Error('Alege perioada.')
      await api.put(`/discounturi/campanii/incadrare/${encodeURIComponent(docNo)}`, {
        tipCampanie: tip,
        ...p,
      })
    },
    onSuccess: () => {
      toast.success('Încadrat.')
      qc.invalidateQueries({ queryKey: ['discounturi', 'campanii'] })
      onGata()
    },
    onError: (e: unknown) =>
      toast.error(
        (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
          ?.message ??
          (e as Error).message ??
          'Nu s-a salvat',
      ),
  })
  const sterge = useMutation({
    mutationFn: async () =>
      api.delete(`/discounturi/campanii/incadrare/${encodeURIComponent(docNo)}`),
    onSuccess: () => {
      toast.success('Revine la observația de pe factură.')
      qc.invalidateQueries({ queryKey: ['discounturi', 'campanii'] })
      onGata()
    },
  })

  const camp = 'rounded border border-slate-300 bg-white px-2 py-1 text-sm'
  return (
    <div className="flex flex-wrap items-end gap-2 text-sm">
      <label className="flex flex-col text-xs text-slate-500">
        Tip
        <select
          className={camp}
          value={tip}
          onChange={(e) => setTip(e.target.value as TipCampanie)}
        >
          {TIPURI.map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col text-xs text-slate-500">
        Perioada
        <select
          className={camp}
          value={fel}
          onChange={(e) => setFel(e.target.value as 'luna' | 'trimestru' | 'interval')}
        >
          <option value="luna">O lună</option>
          <option value="trimestru">Un trimestru</option>
          <option value="interval">Interval de zile</option>
        </select>
      </label>
      {fel === 'luna' && (
        <input
          type="month"
          className={camp}
          value={luna}
          onChange={(e) => setLuna(e.target.value)}
        />
      )}
      {fel === 'trimestru' && (
        <>
          <select className={camp} value={trim} onChange={(e) => setTrim(Number(e.target.value))}>
            {[1, 2, 3, 4].map((t) => (
              <option key={t} value={t}>
                T{t}
              </option>
            ))}
          </select>
          <input
            type="number"
            className={`${camp} w-24`}
            value={an}
            onChange={(e) => setAn(Number(e.target.value))}
          />
        </>
      )}
      {fel === 'interval' && (
        <>
          <input
            type="date"
            className={camp}
            value={deZi}
            onChange={(e) => setDeZi(e.target.value)}
          />
          <input
            type="date"
            className={camp}
            value={panaZi}
            onChange={(e) => setPanaZi(e.target.value)}
          />
        </>
      )}
      <button
        className="rounded bg-slate-900 px-3 py-1.5 font-medium text-white hover:bg-slate-700 disabled:opacity-50"
        disabled={salveaza.isPending}
        onClick={() => salveaza.mutate()}
      >
        Salvează
      </button>
      {manual && (
        <button
          className="rounded border border-slate-300 px-3 py-1.5 text-slate-700 hover:bg-slate-100"
          disabled={sterge.isPending}
          onClick={() => sterge.mutate()}
        >
          Revino la observația de pe factură
        </button>
      )}
      <button className="px-2 py-1.5 text-slate-500 hover:underline" onClick={onGata}>
        Renunță
      </button>
    </div>
  )
}
